
<template>
  <div name="registers" class="inner">
    <innertopbar @close="cancel()">
      <template v-slot:breadcrumb>
        <router-link to="events"> Import Pupils </router-link>
        <i class="carret fas fa-caret-right"></i> {{ title }}
      </template>

      <template v-slot:listitems>
        <li
          v-if="allowClassMove"
          @click="deletePupils"
          v-tooltip="{
            content: 'Delete Pupils',
            placement: 'bottom-center',
            offset: 5,
            classes: ['tooltip-info'],
          }"
          title="Delete Pupils"
          class="action-list-item inline-block hover:opacity-100"
        >
          <i class="icon fas fa-trash-alt pt-4" title="Delete"></i>
        </li>

        <li
          v-if="allowClassMove"
          v-on:click="showMove = true"
          v-tooltip="{
            content: 'Move Pupils',
            placement: 'bottom-center',
            offset: 5,
            classes: ['tooltip-info'],
          }"
          title="Move Pupils"
          class="action-list-item inline-block hover:opacity-100"
        >
          <i class="icon fas fa-suitcase-rolling pt-4" title="Move"></i>
        </li>

        <li
          @click="addNew"
          v-tooltip="{
            content: 'Add Pupil',
            placement: 'bottom-center',
            offset: 5,
            classes: ['tooltip-info'],
          }"
          title="Add Pupil"
          class="action-list-item inline-block hover:opacity-100"
        >
          <i class="icon fas fa-plus-circle pt-4" title="Add Pupil"></i>
        </li>

        <li class="inline-block mx-4 text-2xl text-gray-400">|</li>

        <li class="inline-block">
          <button
            v-on:click="importIsVisible = true"
            type="button"
            class="publish-button hover:opacity-75"
          >
            Import Data
          </button>
        </li>
      </template>
    </innertopbar>

    <div class="mt-6 p-6 bg-white rounded-lg mb-4 w-95p mx-auto">
      <h2 class="w-full text-lg mb-2 font-bold text-gray-700">Year Groups</h2>

      <ul class="buttons">
        <li v-for="item in yearFilter" :key="item.id">
          <button
            type="button"
            class="p-2 rounded-lg"
            v-bind:class="checkYear(item)"
            @click="yearSelect(item)"
          >
            {{ item.name }}
          </button>
        </li>
      </ul>
    </div>

    <div class="mt-0 p-6 bg-white rounded-lg mb-10 w-95p mx-auto">
      <savenotice :title="'Pupils'" ref="notice"></savenotice> 

      <ul class="filter">
        <li>
          <label>Classes:</label>
          <dropdown
            :options="classDropdown"
            :selected="selectedClass"
            v-on:updateOption="classSelect"
          ></dropdown>
        </li>
        <li>
          <label>Pupil Premium:</label>
          <dropdown
            :options="activeDropdown"
            :selected="selectedActive"
            v-on:updateOption="activeSelect"
          ></dropdown>
        </li>
        <li>
          <label>Search:</label>
          <input
            type="text"
            class="form-control"
            name="search"
            v-model="search"
            placeholder="Enter search term..."
            style="width: 27em"
          />
        </li>
      </ul>

      <div v-if="errored" class="empty">
        <p>
          We are not able to retrieve this information at the moment, please try
          back later
        </p>
      </div>

      <div id="content-area" v-else>
        <div style="width: 100%; text-align: center" v-if="loading">
          <i class="spinner fa fa-spinner fa-spin"></i>
        </div>

        <div v-else>
          <h4 class="p-4">
            Total: <strong>{{ total }}</strong> pupils in view
          </h4>
          <table
            class="table-wrapper w-full bg-white mx-auto rounded shadow-sm"
            v-if="componentLoaded"
          >
            <thead class="text-primary bg-gray-100 h-12">
              <tr>
                <th width="5%" class="selectable" @click="setSelectAll()">
                  <i
                    class="far"
                    v-bind:class="[
                      selectAll ? 'fa-check-circle on' : 'fa-circle',
                    ]"
                  ></i>
                </th>
                <th width="6%" align="left">Year</th>
                <th width="35%">Name</th>

                <th width="18%">Class</th>
                <th width="25%" align="center">SEN</th>
                <th>PP</th>
                <th width="10%"></th>
              </tr>
            </thead>
            <tbody>
              <tr class="course" v-for="item in filteredList" :key="item.id">
                <td class="selectable" @click="selectPupil(item)">
                  <i
                    class="pl-3 far"
                    v-bind:class="[
                      isSelected(item) ? 'fa-check-circle on' : 'fa-circle',
                    ]"
                  ></i>
                </td>
                <td class="emphasise">
                  <div
                    v-for="year in years" 
                    v-if="year.id == item.year.id"
                    :key="year.id"
                  >
                    {{ year.letter }}
                  </div>
                </td>
                <td>
                  <strong>{{ item.name }}</strong>
                </td>

                <td>{{ item.inclass }}</td>
                <td>{{ item.sen_status }}</td>
                <td>
                  <i
                    class="icon list"
                    v-bind:class="setOnClass(item.premium_pupil)"
                  />
                </td>
                <td align="center" class="tb-actions">
                  <a
                    v-on:click="editPupil(item)"
                    class="text-button orange-text"
                    >Edit</a
                  >
                </td>
              </tr>
            </tbody>
            <tfoot></tfoot>
          </table>

          <p class="empty" v-else>
            We could not find any data for your given selection.
          </p>
        </div>
      </div>
    </div>

    <transition name="modal" v-if="showMove == true">
      <div
        class="modal-mask fixed top-0 left-0 w-full h-full outline-none fade"
      >
        <div
          class="modal-container w-11/12 mt-5 mb-10 mx-auto rounded shadow-lg z-50 h-full bg-white"
        >
          <header class="h-12 p-4 pt-10 pb-8 w-11/12 mx-auto">
            <i
              class="fas fa-times-circle cursor-pointer fixed right-0 items-center mt-0 rounded-2 mr-40 text-black text-4xl z-50"
              @click="showMove = false"
              style="right: -12px"
            ></i>
            <h1 class="text-gray-700 font-bold text-4xl">Move Pupils</h1>
          </header>
          <div class="h-3/4 overflow-y-auto w-11/12 mx-auto mt-16 pb-20">
            <slot name="body">
              <table
                class="table-wrapper w-95p bg-white mx-auto rounded shadow-sm"
                v-if="componentLoaded"
              >
                <thead class="text-primary">
                  <tr>
                    <th width="2%"></th>
                    <th width="4%" align="left">Year</th>
                    <th width="35%">Name</th>
                    <th width="18%">Class</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="course" v-for="pupil in selectedPupils" :key="pupil.identifier">
                    <td class="selectable" @click="selectPupil(pupil)">
                      <i
                        class="far"
                        v-bind:class="[
                          isSelected(pupil)
                            ? 'fa-check-circle on'
                            : 'fa-circle',
                        ]"
                      ></i>
                    </td>
                    <td class="emphasise">
                      <div
                        v-for="year in years" :key="year.id"
                        v-if="year.id == pupil.year.id"
                      >
                        {{ year.letter }}
                      </div>
                    </td>
                    <td>
                      <strong>{{ pupil.name }}</strong>
                    </td>

                    <td>{{ pupil.inclass }}</td>
                  </tr>
                </tbody>
                <tfoot></tfoot>
              </table>

              <div class="w-11/12 ml-8 mt-10">
                <h4 class="text-base font-bold">Set new yeargroup:</h4>
                <a
                  class="text-xs cursor-pointer right text-red-400 mb-2"
                  @click="yearMoveSelected = {}"
                  >Clear</a
                >
                <ul class="mt-6 mb-1 w-full">
                  <li
                    v-for="item in years"
                    :key="item.id"
                    class="inline-block mr-2"
                  >
                    <button
                      type="button"
                      @click="yearMoveSelected = item"
                      class="mb-2 hover:bg-gray-400 hover:bg-opacity-50 hover:text-action-blue font-bold py-2 px-4 rounded"
                      v-bind:class="
                        item.id == yearMoveSelected.id
                          ? 'bg-action-blue bg-opacity-75 text-white'
                          : 'bg-gray-100 text-gray-500'
                      "
                    >
                      {{ item.name }}
                    </button>
                  </li>
                </ul>
              </div>

              <div class="ml-8 w-11/12 mt-10">
                <h4 class="text-base font-bold">Set new class:</h4>
                <a
                  class="text-xs cursor-pointer right text-red-400 mb-2"
                  @click="classMoveSelected = {}"
                  >Clear</a
                >
                <ul class="mt-6 mb-1 w-11/12">
                  <li
                    v-for="(item, index) in classes"
                    :key="index"
                    class="inline-block mr-2"
                  >
                    <button
                      type="button"
                      @click="classMoveSelected = item"
                      class="mb-2 hover:bg-gray-400 hover:bg-opacity-50 hover:text-action-blue font-bold py-2 px-4 rounded"
                      v-bind:class="
                        item.id == classMoveSelected.id
                          ? 'bg-action-blue bg-opacity-75 text-white'
                          : 'bg-gray-100 text-gray-500'
                      "
                    >
                      {{ item.name }}
                    </button>
                  </li>
                </ul>
              </div>
              <div class="mt-3 ml-8 w-8/12">
                <label
                  >OR NEW Class
                  <span class="small"
                    >(will overright class selection)</span
                  ></label
                >
                <br />
                <input
                  type="text"
                  class="form-control"
                  v-model="newClass"
                  placeholder="New class name"
                  style="color: #333"
                />
              </div>

              <button
                @click="moveClass()"
                type="button"
                class="move-pupil-btn publish-button hover:opacity-75 mt-10 ml-8 w-8/12"
                :disabled="isMoveDisabled"
              >
                Move Pupils
              </button>
            </slot>
          </div>
        </div>
      </div>
    </transition>

    <registerImport
      :registers="registers"
      :school="school"
      @close="closeImport()"
      v-if="importIsVisible"
      v-show="importIsVisible"
    ></registerImport>


    <ModalSlide
        :show="showingPupilEdit"
        :cssWidth="'md:w-6/12'"
        @closeModal="closePupilEditView()"
      >
        <template v-slot:header-icon>
          <i class="fas fa-calendar-alt"></i>
        </template>
        <template v-slot:header-title> {{ registerTitle }} </template>
        <template v-slot:body>
          <AdminPupilEdit 
          v-if="showingPupilEdit"
          :school="school"
          :pupil="selectedPupil"
          :newAddition="newAddition"
          :schoolType="selectedType"
          :classes="classDropdown"
           @close="closePupilEditView()"
          ></AdminPupilEdit>

        </template>
      </ModalSlide>

  </div>
</template>

<script>
//import { mapGetters } from 'vuex'
import { BaseService } from "@/services/school.service";
const innertopbar = () => import("@/components/InnerTopBar.vue");
const dropdown = () => import("@/components/admin/Dropdown.vue");
const ModalSlide = () => import("@/components/ModalSlide.vue");

const noaccess = () => import("@/components/admin/NoAccess.vue");
const filterbar = () => import("@/components/admin/FilterBar.vue");
const savenotice = () => import("@/components/admin/SaveNotice.vue");
const registerImport = () => import("@/components/admin/TheRegisterImport.vue");
const AdminPupilEdit  = () => import("@/components/admin/AdminRegisterEdit.vue");
import { yearGroupMixin } from "@/mixins/yearGroupMixin.js";

export default {
  name: "registers",
  mixins: [yearGroupMixin],
  props: {
    school: { type: Object, required: true },
  },
  computed: {
    // ...mapGetters({ currentUser: 'currentUser', currentAccess: 'currentAccess' }),
    componentLoaded() {
      return this.filteredList.length > 0;
    },
    title() {
      return this.school.name;
    },
    registerTitle() {
      if (this.newAddition) {
        return "Adding new pupil"
      } else {
        return "Editing " + this.selectedPupil.name
      }
    },
    allowClassMove() {
      return this.selectedPupils.length > 0;
    },
    activeDropdown() {
      this.selectedActive = this.actives[0];
      return this.actives;
    },
    isMoveDisabled() {
      return (
        this.yearMoveSelected.id == undefined &&
        this.classMoveSelected.id == undefined &&
        this.newClass.length == 0
      );
    },
    classDropdown() {
      let list = _.orderBy(this.classes, ["name"]);
      var results = [{ id: 0, name: "Classes" }].concat(list);
      this.selectedClass = results[0];
      return results;
    },
    orderedRegisters() {
      var vm = this;
      var active = vm.selectedActive.id;
      var yeargroups = vm.selectedYears;
      var classChosen = vm.selectedClass;

      if (active == 2 && yeargroups.length == 0 && classChosen.id == 0) {
        return _.orderBy(vm.registers, ["yeargroup", "class", "name"]);
      } else {
        var filtered = vm.registers.filter(function (item) {
          let filtered = true;

          if (active < 2) {
            filtered = item.premium_pupil == active;
          }

          if (filtered && yeargroups.length > 0) {
            filtered = yeargroups.includes(item.yeargroup);
          }

          if (filtered) {
            if (classChosen.id > 0) {
              filtered = item.inclass === classChosen.name;
            }
          }

          return filtered;
        });

        return _.orderBy(filtered, ["yeargroup", "inclass", "name"]);
      }
    },
    filteredList() {
      return this.orderedRegisters.filter((item) => {
        var vm = this;
        return (
          item.name.toLowerCase().includes(vm.search.toLowerCase()) ||
          item.inclass.toLowerCase().includes(vm.search.toLowerCase())
        );
      });
    },
    total() {
      return this.filteredList.length;
    },
  },
  data() {
    return {
      loading: true,
      errored: false,
      registers: [],
      yearFilter: [],
      classes: [],
      actives: [
        { id: 2, name: "All" },
        { id: 0, name: "No Pupil Premium" },
        { id: 1, name: "Pupil Premium" },
      ],
      selectedActive: {},
      selectedYears: [],
      search: "",
      selectedType: {},
      selectedClass: {},
      yearMoveSelected: {},
      classMoveSelected: {},
      selectedPupils: [],
      selectAll: false,
      showMove: false,
      importIsVisible: false,
      newClass: "",
      showingPupilEdit: false,
      selectedPupil: {},
      newAddition: false

    };
  },
  created() {
    this.loading = true;
    if (this.school === undefined) {
      this.cancel()
    } else {
      this.fetchData();
    }
   
  },
  mounted() {},
  methods: {
    fetchData(type) {
      this.yearFilter = this.years;

      BaseService.getAll("archon/office/schools/" + this.school.id + "/pupils")
        .then((response) => this.buildList(response.data.data))
        .catch((err) => {
          console.log("Registers " + err);
          this.errored = true;
        })
        .finally(() => (this.loading = false));
    },
    buildList(data) {
      this.registers = data;
      let distinct = new Set(
        data.map(function (item) {
          return item.inclass;
        })
      );

      let arr = Array.from(distinct);

      this.classes = arr.map(function (item, index) {
        return { id: index + 1, name: item };
      });
    },
    setOnClass(value) {
      if (value == 1) {
        return "fas fa-check-circle active-true";
      } else {
        return "fas fa-times-circle active-false";
      }
    },
    activeSelect(active) {
      this.selectedActive = active;
    },
    classSelect(value) {
      this.selectedClass = value;
    },
    cancel() {
      this.$router.push({ name: "SchoolsAdmin" });
      window.scrollTo(0, 0);
    },
    back() {
      this.$router.push({
        name: "SchoolEdit",
        params: { school: this.school },
      });
      window.scrollTo(0, 0);
    },
    yearSelect(year) {
      let key = year.value;
      if (this.selectedYears.includes(key)) {
        this.selectedYears.splice(this.selectedYears.indexOf(key), 1);
      } else {
        this.selectedYears.push(key);
      }
    },
    checkYear(year) {
      return this.selectedYears.includes(year.value) ? "on" : "";
    },
    selectPupil(pupil) {
      if (this.selectedPupils.some((p) => p.identifier === pupil.identifier)) {
        this.selectedPupils.splice(
          this.selectedPupils.findIndex(
            (item) => item.identifier === pupil.identifier
          ),
          1
        );
      } else {
        this.selectedPupils.push(pupil);
      }
    },
    isSelected(pupil) {
      if (this.selectedPupils.some((p) => p.identifier === pupil.identifier)) {
        return true;
      } else {
        return false;
      }
    },
    setSelectAll() {
      this.selectAll = !this.selectAll;
      if (this.selectAll == false) {
        this.selectedPupils = [];
      } else {
        this.selectedPupils = [];
        this.selectedPupils = this.selectedPupils.concat(this.filteredList);
      }
    },
    closePupilEditView() {
      this.showingPupilEdit = false;
    },
    addNew() {
      this.selectedPupil = {}
      this.newAddition = true
      this.showingPupilEdit = true;
    },
    editPupil(pupil) {
      this.selectedPupil = pupil
      this.newAddition = false
      this.showingPupilEdit = true;
    },
    closeImport() {
      this.importIsVisible = false;
      this.$refs.notice.saved();
      this.loading = true;
      this.fetchData();
    },
    moveClass() {
      this.showMove = false;
      let vm = this;
      vm.selectedPupils.forEach(function (pp, index) {
        if (vm.newClass.length > 0) {
          pp.inclass = vm.newClass;
        } else if (
          vm.classMoveSelected.id != undefined &&
          vm.classMoveSelected.id > 0
        ) {
          pp.inclass = vm.classMoveSelected.name;
        } else {
          pp.inclass = pp.inclass;
        }

        if (vm.yearMoveSelected.id != undefined) {
          pp.year_id = vm.yearMoveSelected.id;
        } else {
          pp.year_id = pp.year.id;
        }

        BaseService.update(
          "archon/office/schools/" + vm.school.id + "/pupils/" + pp.identifier,
          pp
        )
          .then((response) => {
            if (index == vm.selectedPupils.length - 1) {
              vm.selectedPupils = [];
            }
            vm.yearMoveSelected = {};
            vm.classMoveSelected = {};
            vm.newClass = "";
            vm.$refs.notice.saved();
            vm.fetchData()
          })
          .catch((err) => {
            console.log(err)
            vm.failure(err);
          });
      });
    },
    deletePupils() {
      this.$swal({
        title: "Are you sure you want to delete?",
        text: "You will not be able to revert this!",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete!",
      }).then((result) => {
        if (result.value === true) {
          this.processDelete();
        }
      });
    },
    processDelete() {
      let vm = this;
      vm.selectedPupils.forEach(function (pp, index) {
        BaseService.delete(
          "archon/office/schools/" + vm.school.id + "/pupils/" + pp.identifier,
          null
        )
          .then((response) => {
            if (index == vm.selectedPupils.length - 1) {
              vm.selectedPupils = [];
              vm.closeImport();
            }
          })
          .catch((err) => {
            console.log(err);
          });
      });
    },
  },
  components: {
    innertopbar,
    dropdown,
    noaccess,
    filterbar,
    registerImport,
    savenotice,
    ModalSlide,
    AdminPupilEdit
  },
};
</script>


<style scoped>
table td {
  padding-top: 0.5em !important;
  padding-bottom: 0.5em !important;
}

ul.filter {
  width: 100%;
  margin-left: 1em;
}

.filter li {
  width: 30%;
  padding-right: 0em !important;
  display: inline-block;
}

.spinner {
  color: #f56a6a !important;
  font-size: 14em;
}

.tb-actions a,
.selectable {
  cursor: pointer !important;
}

.selectable {
  padding-left: 11px;
}

.selectable i {
  font-size: 20px;
}

.selectable i.on {
  color: #f0ad4e !important;
}

.emphasise {
  font-size: 20px;
  font-weight: 700;
  padding-left: 14px;
}

.modal-body {
  overflow-y: scroll;
  height: 85% !important;
}

.modal-body .table {
  margin-left: 2px;
}

.modal-body .table td,
.modal-body .table th {
  color: #333 !important;
  border-bottom: 1px #cdcdcd solid !important;
  padding: 7px !important;
  font-size: 90%;
}

.modal-body .table tr:nth-child(2n) {
  background-color: rgba(230, 235, 237, 0.8) !important;
}

.modal-body .table td {
}

.modal-body h4 {
  color: #333 !important;
}

.card-header ul {
  width: 50%;
  float: right;
  right: 0;
  text-align: right !important;
}

.card-header ul li {
  display: inline-block;
  padding-right: 17px;
}

.card-header ul li a:hover {
  opacity: 0.7 !important;
}

/*****************************
 **** Filter
 ***************************/

ul.filter {
  width: 100%;
}

.filter li {
  width: 30%;
  padding-right: 0em !important;
  display: inline-block;
}

.filter li:first-of-type {
  width: 17%;
}

ul.buttons li {
  display: inline-block;
}

ul.buttons li button {
  margin: 4px;
  border-width: 1px;
  cursor: pointer;
}

ul.buttons li button:hover {
  opacity: 0.7 !important;
}

ul.buttons li button.on {
  background-color: #e14eca !important;
  border-color: #e14eca !important;
  color: #fff;
}

.form-control {
  display: block;
  width: 100%;
  height: calc(2.25rem + 2px);
  padding: 0.5rem 0.7rem;
  font-size: 0.875rem;
  line-height: 1.428571;
  color: rgba(255, 255, 255, 0.8);
  background-color: transparent;
  background-clip: padding-box;
  border: 1px solid #cad1d7;
  border-radius: 0.25rem;
  box-shadow: none;
  transition: all 0.2s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@media screen and (prefers-reduced-motion: reduce) {
  .form-control {
    transition: none;
  }
}

.form-control::-ms-expand {
  background-color: transparent;
  border: 0;
}

.form-control:focus {
  color: rgba(255, 255, 255, 0.8);
  background-color: #ffffff;
  border-color: rgba(50, 151, 211, 0.25);
  outline: 0;
  box-shadow: none, none;
}

.form-control::placeholder {
  color: #adb5bd;
  opacity: 1;
}

.form-control:disabled,
.form-control[readonly] {
  background-color: #e9ecef;
  opacity: 1;
}

select.form-control:focus::-ms-value {
  color: rgba(255, 255, 255, 0.8);
  background-color: transparent;
}

.form-control-file,
.form-control-range {
  display: block;
  width: 100%;
}
</style>
